import { useEffect, useRef, useState } from "react";
import { useScrollReveal } from "@/hooks/useScrollReveal";

function easeOutQuart(x: number): number {
  return 1 - Math.pow(1 - x, 4);
}

const CountUp = ({
  target,
  suffix = "",
  duration = 1200,
}: {
  target: number;
  suffix?: string;
  duration?: number;
}) => {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const hasAnimated = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated.current) {
          hasAnimated.current = true;
          const start = performance.now();
          const update = (time: number) => {
            const progress = Math.min((time - start) / duration, 1);
            const eased = easeOutQuart(progress);
            setCount(Math.round(eased * target));
            if (progress < 1) requestAnimationFrame(update);
          };
          requestAnimationFrame(update);
        }
      },
      { threshold: 0.5 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target, duration]);

  return (
    <div
      ref={ref}
      className="font-black leading-none tracking-tight"
      style={{
        fontSize: "clamp(3.5rem, 7vw, 5.5rem)",
        color: "var(--color-preto-marca)",
      }}
    >
      {count}
      {suffix}
    </div>
  );
};

const stats = [
  {
    value: 6,
    suffix: "+",
    label: "empresas atendidas",
    note: "EBNs de Navegação Interior e Cabotagem",
  },
  {
    value: 2,
    suffix: "",
    label: "estados com presença",
    note: "Pará e Amazonas",
  },
];

const Clients = () => {
  const sectionRef = useScrollReveal();

  return (
    <section
      id="clientes"
      ref={sectionRef as React.RefObject<HTMLElement>}
      className="py-24 relative overflow-hidden bg-dots"
      style={{ background: "var(--color-branco-vela)" }}
    >
      <div className="container mx-auto px-4 lg:px-6 relative">
        {/* Header */}
        <div className="max-w-3xl mx-auto text-center mb-16" data-animate>
          <div className="eyebrow justify-center">Resultados</div>
          <h2
            className="heading-h2 mb-4"
            style={{ color: "var(--color-preto-marca)" }}
          >
            Empresas que já transformaram seu AFRMM com a SIATECH.
          </h2>
          <div
            className="w-14 h-px mx-auto mb-6"
            style={{ background: "var(--color-cinza-nevoa)" }}
          />
          <p
            className="text-base font-light leading-relaxed max-w-2xl mx-auto"
            style={{ color: "var(--color-cinza-ancora)" }}
          >
            De empresas que nunca solicitaram AFRMM a operadores experientes que
            precisavam de escala — todas encontraram no processo SIATECH uma
            forma mais inteligente de operar.
          </p>
        </div>

        {/* Stats */}
        <div
          className="flex flex-col sm:flex-row items-center justify-center max-w-2xl mx-auto mb-16 gap-10 sm:gap-0"
          data-animate
        >
          {stats.map((stat, index) => (
            <div key={index} className="flex items-stretch">
              {index > 0 && (
                <div
                  className="hidden sm:block w-px mx-10 self-stretch"
                  style={{ background: "var(--color-cinza-nevoa)" }}
                />
              )}
              <div className="text-center">
                <CountUp target={stat.value} suffix={stat.suffix} />
                <div
                  className="text-sm font-medium mt-2"
                  style={{ color: "var(--color-preto-marca)" }}
                >
                  {stat.label}
                </div>
                <div
                  className="text-xs mt-1 font-light"
                  style={{ color: "var(--color-cinza-ancora)" }}
                >
                  {stat.note}
                </div>
              </div>
            </div>
          ))}

          {/* Separator + ressarcidos */}
          <div className="flex items-stretch">
            <div
              className="hidden sm:block w-px mx-10 self-stretch"
              style={{ background: "var(--color-cinza-nevoa)" }}
            />
            <div className="text-center">
              <div
                className="font-black leading-none tracking-tight"
                style={{
                  fontSize: "clamp(3.5rem, 7vw, 5.5rem)",
                  color: "var(--color-preto-marca)",
                }}
              >
                5min
              </div>
              <div
                className="text-sm font-medium mt-2"
                style={{ color: "var(--color-preto-marca)" }}
              >
                tempo de processamento
              </div>
              <div
                className="text-xs mt-1 font-light"
                style={{ color: "var(--color-cinza-ancora)" }}
              >
                antes levava mais de 1 dia
              </div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center" data-animate>
          <p
            className="text-base font-light mb-6"
            style={{ color: "var(--color-cinza-ancora)" }}
          >
            Sua empresa pode ser a próxima a operar com eficiência real.
          </p>
          <a href="#contato" className="btn-outline-light inline-flex">
            Solicitar Análise Gratuita
          </a>
        </div>
      </div>
    </section>
  );
};

export default Clients;
